import { useCallback, useEffect, useRef, useState } from "react";
import { useRaceWarmupMutation } from "../api/artOfTheDayApi";

/**
 * The race clock: server time on this device, plus the warm-up requests that
 * keep the tap fast. The race button turns green by this clock. Nothing on
 * the page shows a countdown (owner's call, 2026-11-03).
 *
 * Why it exists (2026-11-03 race timing audit): the page used to show only
 * "available at 10:50", so racers timed the tap off their own device clock.
 * Android clocks were off the server by more than 500ms on 37% of taps (up to
 * 2.3s), so they tapped a second early or late without knowing. This hook
 * syncs to the server the way NTP does: send a request, read the server's
 * time from the answer, assume it was stamped halfway through the round trip,
 * and keep the sample with the fastest round trip (least room for error).
 *
 * The sync requests are `raceWarmup` hits on the exact `/today` URL the tap
 * uses, so they also cache the CORS preflight and keep the connection (and a
 * phone's radio) awake. Without them the tap paid for a fresh handshake plus
 * an OPTIONS round trip that the race's latency correction can't see. The
 * `/today` answers before unlock (`not_started`, `entered_early`) carry the
 * server's time too (`noteServerTime`): a fallback for a page opened in the
 * last second, when not even one warm-up may go out.
 *
 * Known gap: a page opened 600 to ~597s before unlock makes its preflight
 * entry with the page-load `/today`, the entry expires right at unlock, and
 * the warm-ups in between are cache hits that don't renew it. That tap pays
 * the OPTIONS round trip.
 */

export interface RaceClock {
  /** Server time now, estimated on this device (epoch ms). */
  serverNowMs: number;
  /** ms until unlock by the server clock; null without a scheduled time. */
  msToUnlock: number | null;
  /** True once a server time has been read. Before that it's the device clock. */
  synced: boolean;
  /** How far this device's clock runs ahead of the server's (negative = behind). */
  clockAheadMs: number | null;
}

interface ClockSample {
  /** server − device, ms */
  offsetMs: number;
  rttMs: number;
  atMs: number;
  /** From a `/today` answer rather than a warm-up (see `noteServerTime`). */
  fromToday: boolean;
}

// Shared across mounts: a remount (route change, StrictMode) keeps the sync.
let bestSample: ClockSample | null = null;
const SAMPLE_MAX_AGE_MS = 10 * 60_000;
// Mounted clocks, told when the offset moves so the repaint that lands on
// unlock is rescheduled by the new clock.
const syncListeners = new Set<() => void>();

function acceptSample(sample: ClockSample): void {
  const best = bestSample;
  if (best && sample.atMs - best.atMs <= SAMPLE_MAX_AGE_MS) {
    // Any warm-up beats a /today answer; within a kind the faster trip wins.
    if (sample.fromToday && !best.fromToday) return;
    if (sample.fromToday === best.fromToday && sample.rttMs > best.rttMs) return;
  }
  bestSample = sample;
  syncListeners.forEach((listener) => listener());
}

/** True until a warm-up has answered (a /today answer alone doesn't count). */
function needsWarmupSample(): boolean {
  return !bestSample || bestSample.fromToday;
}

/** Server time now, by the best sync sample (device clock when unsynced). */
export function raceServerNow(): number {
  return Date.now() + (bestSample?.offsetMs ?? 0);
}

/** Device clock minus server clock, ms; null until synced. */
export function raceClockAheadMs(): number | null {
  return bestSample ? -bestSample.offsetMs : null;
}

/**
 * Take a sync sample from an answer that carries `serverNow`. `sentWallMs` is
 * `Date.now()` when the request left and `rttMs` the measured round trip.
 *
 * A warm-up's server time is taken as stamped halfway through the trip. A
 * `/today` answer (`fromToday`) may have paid a CORS preflight or a fresh
 * connection before the request really left, which would put the halfway
 * point too early and arm the race button early (then the tap comes back
 * `entered_early`). So its time is taken as stamped when the answer arrived:
 * the button can then only arm late, by at most the answer's trip back.
 */
export function noteServerTime(sample: {
  serverNow: unknown;
  sentWallMs: number;
  rttMs: number;
  fromToday?: boolean;
}): void {
  const { serverNow, sentWallMs, rttMs, fromToday = false } = sample;
  if (typeof serverNow !== "number" || !Number.isFinite(serverNow)) return;
  acceptSample({
    offsetMs: serverNow - (sentWallMs + (fromToday ? rttMs : rttMs / 2)),
    rttMs,
    atMs: Date.now(),
    fromToday,
  });
}

// Warm-up cadence, by time left to unlock.
// Periodic warm-ups start 9.5 minutes out. Before that one sync is enough. It
// also keeps the preflight cache honest: browsers hold a preflight for at most
// 600s (the `maxAge` the backend sends; Safari caps it there too) and a cache
// hit doesn't extend it, so the first warm-up inside this window makes an entry
// that is still good 30s after unlock.
const CADENCE_START_MS = 9.5 * 60_000;
const FAR_INTERVAL_MS = 30_000; // from 9.5 min to 90s out: keep the sync fresh
const NEAR_WINDOW_MS = 90_000;
const NEAR_INTERVAL_MS = 5_000; // last 90s: keep the connection warm
const LAST_WARMUP_BEFORE_MS = 2_000; // a final one, done before the tap
// A page with no warm-up sample yet may still send one this close to unlock:
// the preflight and connection are warm from its /today, so it answers long
// before the tap.
const FIRST_WARMUP_MIN_LEFT_MS = 1_000;
const STOP_AFTER_UNLOCK_MS = 5_000;
const INITIAL_BURST = 3;
const INITIAL_BURST_SPACING_MS = 400;
// Repaint cadence. Nothing shows the time, so a render is only needed when
// something flips: the button at unlock (always hit exactly) and the art hold
// a few seconds either side of it. Every 250ms near unlock, every 5s before.
const NEAR_TICK_WINDOW_MS = 10_000;
const NEAR_TICK_MS = 250;
const FAR_TICK_MS = 5_000;

export function useRaceClock(scheduledTime: string | null | undefined, enabled: boolean): RaceClock {
  const scheduledMs = scheduledTime ? Date.parse(scheduledTime) : NaN;
  const active = enabled && Number.isFinite(scheduledMs);
  const [warmup] = useRaceWarmupMutation();
  const [tickMs, setTickMs] = useState<number>(() => raceServerNow());
  // The unlock `tickMs` was last computed for. On the render where the clock
  // turns on (or the unlock changes), tickMs is from an older tick, so that
  // render reads the clock directly instead of a stale reading.
  const tickForRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);

  const warm = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const sentPerf = performance.now();
    const sentWallMs = Date.now();
    try {
      const res = await warmup().unwrap();
      noteServerTime({
        serverNow: res?.serverNow,
        sentWallMs,
        rttMs: performance.now() - sentPerf,
      });
    } catch {
      // A failed warm-up only costs the sync; the button falls back to the device clock.
    } finally {
      inFlightRef.current = false;
    }
  }, [warmup]);

  // Warm-up schedule: a short burst now (the first one pays for the preflight,
  // the next ones give a clean round trip), then by distance to unlock.
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const scheduleNext = () => {
      if (cancelled) return;
      const left = scheduledMs - raceServerNow();
      if (left < -STOP_AFTER_UNLOCK_MS) return;
      let delay: number;
      if (left > CADENCE_START_MS) {
        delay = left - CADENCE_START_MS;
      } else if (left > NEAR_WINDOW_MS) {
        delay = Math.min(FAR_INTERVAL_MS, left - NEAR_WINDOW_MS + NEAR_INTERVAL_MS);
      } else if (left > LAST_WARMUP_BEFORE_MS + NEAR_INTERVAL_MS) {
        delay = NEAR_INTERVAL_MS;
      } else if (left > LAST_WARMUP_BEFORE_MS) {
        delay = left - LAST_WARMUP_BEFORE_MS;
      } else {
        // Past the last warm-up: nothing may share the connection with the tap.
        return;
      }
      timer = setTimeout(async () => {
        await warm();
        scheduleNext();
      }, Math.max(0, delay));
    };

    void (async () => {
      for (let i = 0; i < INITIAL_BURST && !cancelled; i += 1) {
        const minLeft =
          i === 0 && needsWarmupSample() ? FIRST_WARMUP_MIN_LEFT_MS : LAST_WARMUP_BEFORE_MS;
        if (scheduledMs - raceServerNow() < minLeft) break;
        await warm();
        if (i < INITIAL_BURST - 1) await new Promise((r) => setTimeout(r, INITIAL_BURST_SPACING_MS));
      }
      scheduleNext();
    })();

    // A phone that comes back from the background has a cold connection and
    // possibly a moved clock: warm up again, unless unlock is right now.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (scheduledMs - raceServerNow() > LAST_WARMUP_BEFORE_MS) void warm();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [active, scheduledMs, warm]);

  // Repaint, landing exactly on unlock.
  useEffect(() => {
    if (!active) return undefined;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = () => {
      const now = raceServerNow();
      tickForRef.current = scheduledMs;
      setTickMs(now);
      const left = scheduledMs - now;
      const interval = Math.abs(left) < NEAR_TICK_WINDOW_MS ? NEAR_TICK_MS : FAR_TICK_MS;
      // Land a repaint exactly on unlock so the race button arms on time,
      // not up to one tick late.
      timer = setTimeout(tick, left > 0 ? Math.min(interval, left) : interval);
    };
    const retick = () => {
      if (timer) clearTimeout(timer);
      tick();
    };
    tick();
    syncListeners.add(retick);
    return () => {
      syncListeners.delete(retick);
      if (timer) clearTimeout(timer);
      tickForRef.current = null;
    };
  }, [active, scheduledMs]);

  const synced = bestSample !== null;
  const nowMs = tickForRef.current === scheduledMs ? tickMs : raceServerNow();
  return {
    serverNowMs: nowMs,
    msToUnlock: active ? scheduledMs - nowMs : null,
    synced,
    clockAheadMs: synced ? raceClockAheadMs() : null,
  };
}
